/* List.tsx */
.listWithSearch {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

.listContent {
  flex: 1;
  overflow: hidden;
  position: relative;
  transition: opacity 0.2s ease;
}

.listContent[data-loading='true'] {
  opacity: 0;
}

/* ListBox.tsx */
.scrollView {
  contain: paint;
  height: 100%;
  overflow-y: auto;
  width: 100%;
}

.scrollView[data-root-drop-target] {
  box-shadow: inset 0 0 0 2px var(--colors-brand-primary-alpha-medium);
}

.listBox {
  margin: 0;
  outline: none;
  padding: 0;
  position: relative;
}

/* ListBoxSection.tsx */
.section {
  list-style: none;
  padding: 0;
  position: absolute;
  width: 100%;
}

.sectionItems {
  list-style: none;
  padding: 0;
}

.dividerContainer {
  left: 0;
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  width: 100%;
  z-index: 1;
}

/* ListItemContent.tsx */
.itemContentText {
  flex: 1;
  min-width: 0;
  text-decoration: none;
}

.itemContent {
  display: flex;
  flex: 1;
  gap: var(--gap, var(--spaces-12));
  isolation: isolate;
  min-width: 0;
}

.itemContent[data-variant='nav'] {
  gap: var(--gap, var(--spaces-8));
}

.itemContent[data-variant='nav'] .itemContentText {
  align-items: center;
  display: flex;
  gap: var(--spaces-8);
}

.itemContent:has([aria-haspopup='true']) [data-meta] {
  display: none;
}

/* ListSearch.tsx */
.listSearchContainer {
  display: flex;
  flex-direction: column;
  gap: var(--vapor-list-search-gap-y);
  padding-block-end: var(--vapor-list-search-padding-block-end);
  padding-block-start: var(--vapor-list-search-padding-block-start);
  padding-inline-end: var(--vapor-list-search-padding-inline-end);
  padding-inline-start: var(--vapor-list-search-padding-inline-start);
}

/* ListBoxLoading.tsx */
.loadingFooter {
  min-height: 100px;
  position: relative;
  width: 100%;
}

.loadingFill {
  align-items: center;
  display: flex;
  height: 100%;
  inset: 0;
  justify-content: center;
  pointer-events: none;
  position: absolute;
}

/* ListItemDragHandle.tsx */
.dragHandle {
  align-items: center;
  align-self: center;
  border-radius: var(--radii-4);
  color: var(--colors-icon-primary);
  cursor: grab;
  display: flex;
  height: var(--spaces-24);
  justify-content: center;
  width: var(--spaces-24);
}

.dragHandle:focus-visible {
  outline: 1px solid var(--colors-border-interactive-focused);
}

.dragHandle[aria-disabled='true'] {
  color: var(--colors-icon-interactive-disabled);
  cursor: default;
  pointer-events: none;
}

/* ListItemUnreadBadge.tsx */
.unreadBadge {
  border-radius: 50%;
  cursor: pointer;
  height: 24px;
  left: -4px;
  outline: none;
  pointer-events: auto;
  position: absolute;
  top: -4px;
  width: 24px;
}

.unreadBadge::after {
  border: 1px solid var(--colors-border-interactive-hover);
  border-radius: 50%;
  content: '';
  inset: 8px;
  position: absolute;
  transform: scale(1);
  transition: background-color var(--transitions-durations-300)
      var(--transitions-eases-primary),
    border-color var(--transitions-durations-100) linear,
    transform var(--transitions-durations-300)
      var(--transitions-eases-primary);
}

.unreadBadge[data-hovered]::after {
  transform: scale(1.1);
  border-color: var(--colors-border-interactive-hover);
}

.unreadBadge[aria-pressed='false']::after {
  background: var(--colors-meta-opaque-blue);
  border: 1px solid transparent;
}

.unreadBadge[aria-pressed='false'][data-hovered]::after {
  border-color: transparent;
  transform: scale(1.2);
}

.unreadBadge[data-pressed]::after {
  transform: scale(0.6);
}

.unreadBadge[data-focus-visible]::after {
  box-shadow: 0 0 0 1px var(--colors-background-base),
    0 0 0 3px var(--colors-border-interactive-focused);
}

/* ListItemNonSelectable.tsx */
.nonSelectableContents {
  display: contents;
}

/* DropIndicator.tsx */
.dropIndicator {
  opacity: 0;
  pointer-events: none;
  position: absolute;
  z-index: var(--z-indices-modal);
}

.dropIndicator[data-orientation='vertical'] {
  height: 2px;
  left: var(--marginX);
  right: var(--marginX);
  top: var(--y);
  transform: translateY(-50%);
}

.dropIndicator[data-orientation='vertical']::before {
  background: var(--colors-brand-primary);
  border-bottom-left-radius: 0;
  border-radius: 8px;
  border-top-left-radius: 0;
  content: '';
  height: 2px;
  left: 6px;
  position: absolute;
  right: 0;
}

.dropIndicator[data-orientation='vertical'][data-drop-target] {
  opacity: 1;
}

.dropIndicator[data-orientation='vertical']::after {
  border: 2px solid var(--colors-brand-primary);
  border-radius: 50%;
  box-sizing: border-box;
  content: '';
  height: 8px;
  left: 0;
  opacity: 1;
  position: absolute;
  top: calc(50% - 4px);
  width: 8px;
}

.dropIndicator[data-orientation='horizontal'] {
  height: 100%;
  left: var(--x);
  top: var(--y);
  transform: translateX(-50%);
  width: 2px;
}

.dropIndicator[data-orientation='horizontal']::before {
  background: var(--colors-brand-primary);
  border-radius: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  bottom: 0;
  content: '';
  position: absolute;
  top: 8px;
  width: 2px;
}

.dropIndicator[data-orientation='horizontal'][data-drop-target] {
  opacity: 1;
}

.dropIndicator[data-orientation='horizontal']::after {
  border: 2px solid var(--colors-brand-primary);
  border-radius: 50%;
  box-sizing: border-box;
  content: '';
  height: 8px;
  left: calc(50% - 4px);
  opacity: 1;
  position: absolute;
  top: 0;
  width: 8px;
}

/* ListBoxItem.tsx - base styles */
.listBoxItem {
  cursor: pointer;
  outline: none;
  position: absolute;
  text-decoration: none;

  /* Interactive states (shared by nav + list variants) */
  &:where([data-variant='nav'], [data-variant='list']) {
    &:where([data-hovered]:not([aria-selected='true'])) {
      background: var(--colors-background-interactive-hover);
    }

    &:where(:focus-visible, [data-focus-visible]) {
      background: var(--colors-background-interactive-hover);
      box-shadow: inset 0 0 0 1px var(--colors-border-interactive-focused);
    }

    &:where([aria-selected='true']) {
      background: var(--colors-background-interactive-activated-solid);
    }

    &:where([data-drop-target]) {
      background: var(--colors-brand-primary-alpha-low);
      box-shadow: inset 0 0 0 1px var(--colors-border-interactive-selected);
    }

    &:where([data-dragging]) {
      opacity: 0.4;
    }

    &:where([data-disabled]) {
      cursor: default;
    }

    &:where([data-selection-mode='none']) {
      cursor: default;
    }

    /* Rounded corner collapsing for consecutive selected items */
    &:where([data-selected]) {
      &:where([data-next-selected]:not([data-next-gap])) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      &:where([data-prev-selected]:not([data-prev-gap])) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }

  /* Nav variant */
  &:where([data-variant='nav']) {
    align-items: center;
    border-radius: var(--radii-6);
    color: var(--colors-icon-primary);
    display: flex;
    gap: var(--spaces-8);
    padding: var(--spaces-4) var(--spaces-6);
  }

  /* List variant */
  &:where([data-variant='list']) {
    border-radius: var(--radii-8);
    display: flex;
    padding: var(--spaces-8);
  }
}

.listBoxItem[href],
.listBoxItem[data-href] {
  -webkit-touch-callout: none;
  text-decoration: none;
}

.gridCell {
  display: contents;
}

.backgroundLink {
  inset: 0;
  position: absolute;
}

